import React, { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SpotInfo, SpotLeaf, BellIcon, ChevronDown, X } from "design_system/Icon";
import classNames from "classnames";
import { Button, IconButton } from "design_system/Button";

import { useSuppressPaperlessNudgeForCustomer } from "../../hooks/mutations/useSuppressPaperlessNudgeForCustomer";

type BannerStyle = "gray" | "blue";
type Props = {
    bannerStyle?: BannerStyle;
    content: string;
    isSearchParam: boolean;
    linkInternal?: boolean;
    onClose?: () => void;
    searchParamValue: string;
    showIcon?: boolean;
    iconName?: string;
    takeAction: boolean;
    title?: string;
    urlParam?: string;
    userPublicId: string;
};

const BLUE = "blue";
const GRAY = "gray";
const ICON = {
    INFO: "info",
    LEAF: "leaf",
};

export const WelcomeBanner = ({
    bannerStyle = BLUE,
    content,
    isSearchParam = false,
    linkInternal,
    onClose,
    searchParamValue = "",
    showIcon = true,
    iconName = ICON.INFO,
    takeAction = false,
    title,
    urlParam = "",
    userPublicId,
}: Props) => {
    const [visible, setVisible] = useState(true);
    const [suppressPaperlessNudgeForCustomer] = useSuppressPaperlessNudgeForCustomer({
        variables: { userPublicId },
    });
    const contentRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(true);
    const [open, setOpen] = useState(false);
    const id = useId();
    const buttonId = `button-${id}`;
    const panelId = `panel-${id}`;

    const resolvedUrl = useMemo(() => {
        if (linkInternal && isSearchParam) {
            const url = new URL(window.location.href);
            url.searchParams.append(urlParam, searchParamValue);
            return url.href;
        }
        return urlParam;
    }, [linkInternal, isSearchParam, urlParam, searchParamValue]);

    const isPaperlessBanner = urlParam === "paperless-modal";
    const isAlwaysExpanded = isPaperlessBanner || !isTruncated;
    const isExpanded = isAlwaysExpanded || open;
    const showInlineClose = isAlwaysExpanded && !isPaperlessBanner && !takeAction;

    /*  Determine whether the banner text overflows its container.
        If the text fits without truncation, the banner locks open and the chevron is hidden.
        Runs before paint to avoid a visible layout shift.
        Skips measurement for paperless banners (always expanded) and in JSDOM (dimensions are 0) to preserve default behavior in tests.
    */
    useLayoutEffect(() => {
        const contentContainer = contentRef.current;
        if (!contentContainer || isPaperlessBanner) return;
        if (contentContainer.scrollWidth === 0 && contentContainer.clientWidth === 0) return;
        // Safari reports scrollHeight as the clamped height when -webkit-line-clamp is active.
        // Temporarily unset the clamp to measure the true content height.
        contentContainer.style.webkitLineClamp = "unset";
        const fullHeight = contentContainer.scrollHeight;
        contentContainer.style.webkitLineClamp = "";
        const truncated =
            contentContainer.scrollWidth > contentContainer.clientWidth ||
            fullHeight > contentContainer.clientHeight;
        setIsTruncated(truncated);
    }, [content, title, isPaperlessBanner]);

    if (visible) {
        return (
            <div
                className={classNames({
                    "bg-primary-3667e9 text-white": bannerStyle === BLUE,
                    "bg-gray-f4f5f7 text-black": bannerStyle === GRAY,
                })}
            >
                <div role="banner" className="max-w-1100 m-auto p-16 s768:p-24">
                    <div className="flex items-start">
                        {showIcon && (
                            <div className="mr-8 s768:mr-16">
                                {bannerStyle === BLUE ? (
                                    <BellIcon size={24} aria-hidden="true" />
                                ) : (
                                    <>
                                        {iconName === ICON.INFO && (
                                            <SpotInfo
                                                strokeWidth="0.5"
                                                spotColor="#F4F5F7"
                                                color="#65657B"
                                                size={24}
                                                aria-hidden="true"
                                            />
                                        )}
                                        {iconName === ICON.LEAF && (
                                            <SpotLeaf
                                                size={24}
                                                spotColor="#C9DBFE"
                                                strokeWidth="0.5"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <button
                                id={buttonId}
                                type="button"
                                aria-expanded={!showInlineClose ? isExpanded : undefined}
                                aria-controls={isExpanded && !showInlineClose ? panelId : undefined}
                                className={classNames("w-full text-left flex items-start", {
                                    "cursor-default": isAlwaysExpanded,
                                })}
                                onClick={() => !isAlwaysExpanded && setOpen((prev) => !prev)}
                            >
                                <span
                                    ref={contentRef}
                                    className={classNames("flex-1 min-w-0", {
                                        "line-clamp-2 s768:line-clamp-none s768:truncate":
                                            !isExpanded,
                                    })}
                                >
                                    {title && (
                                        <>
                                            <span className="font-500 text-0_875 s768:text-1">
                                                {title}:
                                            </span>{" "}
                                        </>
                                    )}
                                    <span
                                        className={classNames(
                                            "[&_p]:inline [&_a]:font-500 [&_a:hover]:underline font-400 text-0_875 s768:text-1",
                                            {
                                                "[&_a]:text-primary-3667e9": bannerStyle === GRAY,
                                                "[&_a]:text-white": bannerStyle === BLUE,
                                            },
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: content,
                                        }}
                                    />
                                </span>
                                {!isAlwaysExpanded && (
                                    <ChevronDown
                                        className={classNames(
                                            "flex-shrink-0 ml-8 s768:ml-16 transition-transform duration-200",
                                            { "rotate-180 transform": isExpanded },
                                        )}
                                        aria-hidden="true"
                                        size={24}
                                        color={bannerStyle === GRAY ? "#65657B" : undefined}
                                    />
                                )}
                            </button>
                            {isExpanded && !showInlineClose && (
                                <div
                                    id={panelId}
                                    aria-labelledby={buttonId}
                                    className={classNames("mt-16", {
                                        "pr-32 s768:pr-40": !isAlwaysExpanded,
                                    })}
                                >
                                    {!takeAction && (
                                        <div className="flex items-center flex-col s768:flex-row s768:justify-end">
                                            <Button
                                                className={classNames("w-full s768:w-auto", {
                                                    "border-white": bannerStyle === BLUE,
                                                })}
                                                variant={
                                                    bannerStyle === GRAY ? "secondary" : "primary"
                                                }
                                                size="sm"
                                                onClick={async () => {
                                                    setVisible(false);
                                                    onClose?.();
                                                }}
                                            >
                                                {gettext("Dismiss")}
                                            </Button>
                                        </div>
                                    )}
                                    {takeAction && isPaperlessBanner && (
                                        <div className="flex items-center justify-center s768:justify-end">
                                            <Button
                                                className={classNames("pr-28 pl-28 mr-10", {
                                                    "text-white": bannerStyle === BLUE,
                                                })}
                                                variant={
                                                    bannerStyle === BLUE ? "primary" : "tertiary"
                                                }
                                                onClick={async () => {
                                                    setVisible(false);
                                                    await suppressPaperlessNudgeForCustomer();
                                                    onClose?.();
                                                }}
                                            >
                                                {gettext("I'll do it later")}
                                            </Button>
                                            <a
                                                className={classNames(
                                                    "btn btn-small border-gray-e7e9ef border-r-2 px-16 w-auto inline-flex",
                                                    { "btn-secondary ": bannerStyle !== BLUE },
                                                )}
                                                data-patient-app-click="go_paperless_btn"
                                                href={resolvedUrl}
                                            >
                                                {gettext("Go Paperless")}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {showInlineClose && (
                            <IconButton
                                icon={<X />}
                                size={24}
                                aria-label={gettext("Close")}
                                p="p-4"
                                rounded="rounded-4"
                                hover={bannerStyle === BLUE ? "hover:bg-primary-638eff" : undefined}
                                focus={
                                    bannerStyle === BLUE
                                        ? "focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                        : undefined
                                }
                                className="flex-shrink-0 ml-8 s768:ml-16"
                                onClick={() => {
                                    setVisible(false);
                                    onClose?.();
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
    return null;
};
